import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { getColumnId } from "./getColumnId"
import { Column, ColumnType, ColumnWidths } from "./types"
import { useUrlQuery } from "../../../../hooks/useUrlQuery"
import { StartColumnResizeCallback } from "../Table/types"
import { TableCellWidth } from "../TableCell"

const MIN_WIDTH = 36
const Q_PARAM = "colwidths"

export function useTableColumnWidths<T>({
  columns,
  persistInUrl = true,
  initialColumnWidths,
}: {
  columns: Column<T>[]
  persistInUrl?: boolean
  initialColumnWidths?: ColumnWidths
}) {
  const { query: urlQuery, replaceQuery } = useUrlQuery()
  const raw = urlQuery[Q_PARAM]
  const urlColumnWidths = useMemo(() => {
    if (!persistInUrl) return {}
    if (typeof raw !== "string") return {}
    try {
      const parsed: ColumnWidths = JSON.parse(atob(raw))
      return parsed
    } catch {
      return {}
    }
  }, [raw, persistInUrl])

  const defaults = useMemo(() => {
    const initial: ColumnWidths = {}
    for (const col of columns) {
      if (col.type === ColumnType.Group) {
        for (const subCol of col.columns) {
          initial[getColumnId(subCol, col)] = defaultWidths[subCol.width]
        }
        continue
      }
      initial[getColumnId(col)] = defaultWidths[col.width]
    }
    return initial
  }, [columns])

  const [columnWidths, setColumnWidths] = useState({
    ...defaults,
    ...initialColumnWidths,
    ...urlColumnWidths,
  })

  const syncToUrl = useCallback(() => {
    if (!persistInUrl) return
    const nonDefaults: ColumnWidths = {}
    for (const key of Object.keys(columnWidths)) {
      if (columnWidths[key] === defaults[key]) {
        continue
      }
      nonDefaults[key] = columnWidths[key]
    }
    const query = { ...urlQuery }
    if (Object.keys(nonDefaults).length > 0) {
      query[Q_PARAM] = btoa(JSON.stringify(nonDefaults))
    } else {
      delete query[Q_PARAM]
    }
    replaceQuery(query)
  }, [persistInUrl, columnWidths, defaults, urlQuery, replaceQuery])

  const isMissingColumn = useMemo(() => {
    for (const col of columns) {
      if (col.type === ColumnType.Group) {
        for (const subCol of col.columns) {
          if (!columnWidths[getColumnId(subCol, col)]) return true
        }
        continue
      }
      if (!columnWidths[getColumnId(col)]) return true
    }
    return false
  }, [columns, columnWidths])
  if (isMissingColumn) {
    setColumnWidths((prev) => ({
      ...defaults,
      ...prev,
    }))
  }

  const resizingKey = useRef<{
    key: string
    startX: number
    startWidth: number
  }>(null)

  const startColumnResize = useCallback<StartColumnResizeCallback>(
    (key, e) => {
      e.preventDefault()
      resizingKey.current = {
        key,
        startX: e.clientX,
        startWidth: columnWidths[key],
      }
    },
    [columnWidths]
  )

  useEffect(() => {
    function resize(e: globalThis.MouseEvent) {
      if (!resizingKey.current) {
        return
      }

      const newWidth =
        resizingKey.current.startWidth +
        (e.clientX - resizingKey.current.startX)
      if (newWidth >= MIN_WIDTH) {
        setColumnWidths((prev) => {
          if (!resizingKey.current) {
            return prev
          }
          return {
            ...prev,
            [resizingKey.current.key]: newWidth,
          }
        })
      }
    }

    function stopResize() {
      resizingKey.current = null
      syncToUrl()
    }

    window.addEventListener("mousemove", resize)
    window.addEventListener("mouseup", stopResize)

    return () => {
      window.removeEventListener("mousemove", resize)
      window.removeEventListener("mouseup", stopResize)
    }
  }, [setColumnWidths, syncToUrl])

  return {
    columnWidths,
    startColumnResize,
  }
}

export const defaultWidths: Record<TableCellWidth, number> = {
  [TableCellWidth.ExtraExtraSmall]: 36,
  [TableCellWidth.ExtraSmall]: 50,
  [TableCellWidth.Small]: 100,
  [TableCellWidth.Medium]: 120,
  [TableCellWidth.Large]: 160,
  [TableCellWidth.ExtraLarge]: 220,
  [TableCellWidth.ExtraExtraLarge]: 280,
  [TableCellWidth.ExtraExtraExtraLarge]: 340,
  [TableCellWidth.Full]: 400,
}
